/**
 * Alpine component for the upgrade-to-bundle kit upsell card.
 * Config is read from a child <script type="application/json"> tag.
 */
document.addEventListener('alpine:init', () => {
  Alpine.data('upgradeBundleKit', (options = {}) => ({
    isLargeSizeSelected: Boolean(options.hideForLargeSize),
    variant: null,
    sellingPlans: [],
    sellingPlanId: null,
    subscriptionPrice: null,
    variantPrice: null,
    hasSubscription: false,
    moneyFormat: '',
    cartAddUrl: '/cart/add',
    oneTimeSelected: false,
    loading: false,
    sellingPlan: null,
    sellingPlanAllocation: null,

    init() {
      const configEl = this.$el.querySelector('[data-upgrade-bundle-config]');
      if (!configEl) return;

      const config = JSON.parse(configEl.textContent);
      this.variant = config.variant;
      this.sellingPlans = config.sellingPlans || [];
      this.sellingPlanId = config.sellingPlanId;
      this.subscriptionPrice = config.subscriptionPrice;
      this.variantPrice = config.variantPrice ?? config.variant?.price;
      this.hasSubscription = config.hasSubscription;
      this.moneyFormat = config.moneyFormat;
      this.cartAddUrl = config.cartAddUrl || '/cart/add';

      if (Array.isArray(config.sellingPlanAllocations)) {
        this.variant = {
          ...this.variant,
          selling_plan_allocations: config.sellingPlanAllocations,
        };
      }

      this.$nextTick(() => {
        this.syncFromCheckedSizeRadio();
      });

      if (!this.hasSubscription) {
        this.oneTimeSelected = true;
        return;
      }

      this.sellingPlan = this.sellingPlans.find(
        (plan) => Number(plan.id) === Number(this.sellingPlanId),
      ) || this.sellingPlans[0];

      if (this.sellingPlan) {
        this.sellingPlanId = Number(this.sellingPlan.id);
      }

      this.updateSellingPlanAllocation();
    },

    updateVisibilityFromSizeValue(value) {
      if (value == null || value === '') return;
      this.isLargeSizeSelected = String(value).split('(')[0].trim() === 'Large';
    },

    syncFromCheckedSizeRadio() {
      const checkedSizeRadio = document.querySelector(
        'input[type="radio"][name^="product-size"]:checked',
      );

      if (!checkedSizeRadio) return;
      this.updateVisibilityFromSizeValue(checkedSizeRadio.value);
    },

    get shouldAddSellingPlan() {
      return !this.oneTimeSelected && Boolean(this.sellingPlanId);
    },

    updateSellingPlanAllocation() {
      const allocations = this.variant?.selling_plan_allocations;

      if (!allocations?.length || !this.sellingPlanId) {
        this.sellingPlanAllocation = null;
        return;
      }

      const planId = Number(this.sellingPlanId);
      this.sellingPlanAllocation = allocations.find((allocation) => {
        const allocationPlanId = Number(
          allocation.selling_plan_id ?? allocation.selling_plan?.id,
        );
        return allocationPlanId === planId;
      }) || allocations[0];
    },

    getSubscriptionPrice() {
      this.updateSellingPlanAllocation();

      const allocationPrice = this.sellingPlanAllocation?.price;
      if (allocationPrice != null && allocationPrice !== '') {
        return allocationPrice;
      }

      if (this.subscriptionPrice != null && this.subscriptionPrice !== '') {
        return this.subscriptionPrice;
      }

      return this.calculatePriceFromSellingPlan();
    },

    calculatePriceFromSellingPlan() {
      const basePrice = Number(this.variant?.price ?? this.variantPrice);
      if (!Number.isFinite(basePrice)) return null;

      const adjustments = this.sellingPlan?.price_adjustments;
      if (!adjustments?.length) return basePrice;

      return adjustments.reduce((price, adjustment) => {
        if (adjustment.value_type === 'percentage') {
          return Math.round(price * (100 - Number(adjustment.value)) / 100);
        }

        if (adjustment.value_type === 'fixed_amount') {
          return price - Number(adjustment.value);
        }

        if (adjustment.value_type === 'price') {
          return Number(adjustment.value);
        }

        return price;
      }, basePrice);
    },

    get displayPrices() {
      if (this.oneTimeSelected) {
        return {
          sale: this.variant?.price ?? this.variantPrice,
          compare: null,
        };
      }

      const variantPrice = this.variant?.price ?? this.variantPrice;

      return {
        sale: this.getSubscriptionPrice(),
        compare: variantPrice,
      };
    },

    get showComparePrice() {
      if (this.oneTimeSelected) return false;

      const { compare } = this.displayPrices;
      return compare != null && compare !== '';
    },

    toNoDecimalsMoneyFormat(format) {
      if (!format || format.includes('amount_no_decimals')) return format;

      return format
        .replace(
          'amount_with_comma_separator',
          'amount_no_decimals_with_comma_separator',
        )
        .replace('amount', 'amount_no_decimals');
    },

    formatPrice(price) {
      if (price == null || price === '') return '';
      return Shopify.formatMoney(
        price,
        this.toNoDecimalsMoneyFormat(this.moneyFormat),
      );
    },

    toggleOneTimePurchase() {
      this.oneTimeSelected = !this.oneTimeSelected;

      if (!this.oneTimeSelected) {
        this.updateSellingPlanAllocation();
      }
    },

    addBundleToCart() {
      if (!this.variant?.id || this.loading) return;

      const lineItem = {
        id: Number(this.variant.id),
        quantity: 1,
        properties: this.oneTimeSelected
          ? { _onetime: true }
          : { _subscribed: true },
      };

      if (this.shouldAddSellingPlan) {
        lineItem.selling_plan = Number(this.sellingPlanId);
      }

      this.loading = true;

      fetch(`${this.cartAddUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [lineItem] }),
      })
        .then((response) => response.json())
        .then((json) => {
          if (json.status && json.status >= 400) {
            throw new Error(json.description || json.message);
          }

          this.$dispatch('product:added');
          this.$dispatch('cartDrawer:open');
          this.loading = false;
        })
        .catch((error) => {
          console.error(error);
          this.loading = false;
        });
    },
  }));
});
